import { useState } from 'react';
import { BookmarkSimple } from '@phosphor-icons/react';
import { useAuth } from '../../contexts/AuthContext';
import { createBookmark, deleteBookmark } from '../../utils/library';

interface BookmarkButtonProps {
  topicId: string;
  topicSlug: string;
  topicTitle: string;
  bookmarkId?: string | null;
  onToggle?: (bookmarkId: string | null) => void;
  size?: 'sm' | 'md';
}

export default function BookmarkButton({
  topicId,
  topicSlug,
  topicTitle,
  bookmarkId,
  onToggle,
  size = 'md',
}: BookmarkButtonProps) {
  const { currentUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const isBookmarked = !!bookmarkId;

  const handleToggle = async () => {
    if (!currentUser || saving) return;
    setSaving(true);

    try {
      if (isBookmarked && bookmarkId) {
        await deleteBookmark(bookmarkId);
        onToggle?.(null);
      } else {
        const bookmark = await createBookmark({ topicId, topicSlug, topicTitle });
        onToggle?.(bookmark.id);
      }
    } catch (error) {
      console.error('Error toggling bookmark:', error);
    } finally {
      setSaving(false);
    }
  };

  if (!currentUser) return null;

  const iconSize = size === 'sm' ? 18 : 22;

  return (
    <button
      onClick={handleToggle}
      disabled={saving}
      className={`p-1.5 rounded-lg transition-colors ${
        isBookmarked
          ? 'text-pink-500 hover:text-pink-600 bg-pink-50 dark:bg-pink-950'
          : 'text-gray-400 hover:text-pink-500 hover:bg-pink-50 dark:hover:bg-pink-950'
      } ${saving ? 'opacity-50' : ''}`}
      title={isBookmarked ? 'Remove bookmark' : 'Bookmark this topic'}
    >
      <BookmarkSimple size={iconSize} weight={isBookmarked ? 'fill' : 'regular'} />
    </button>
  );
}
